/**
 * Lovable-delivered frozen data contract for the B1.10 DayCloseWizard.
 *
 * Source of truth: Lovable hub (petota-calendar-hub, commit 9dd5b53) on
 * 2026-04-22. DO NOT mutate without a matching Lovable re-delivery — the
 * business app boundary in features/day-close/* consumes this shape.
 *
 * Mirrors the Lovable artifact 1:1. If the feature layer ever needs a
 * richer shape (e.g. future backend), add an adapter in
 * features/day-close/lovable-adapter.ts rather than diverging this file.
 *
 * @see docs/_context/PETOTA_BUSINESS_LOVABLE_BRIEF_B1_10_DAYCLOSE_WIZARD.md
 */

export type DayCloseWizardState =
  | 'draft'
  | 'counting'
  | 'reconciling'
  | 'review'
  | 'submitted'
  | 'closed'
  | 'failed';

export type DayCloseWizardStep =
  | 'prerequisites'
  | 'cash'
  | 'non_cash'
  | 'review'
  | 'submit';

export type TryDenominationCode =
  | 'TRY_200'
  | 'TRY_100'
  | 'TRY_50'
  | 'TRY_20'
  | 'TRY_10'
  | 'TRY_5'
  | 'TRY_1'
  | 'TRY_050'
  | 'TRY_025'
  | 'TRY_010'
  | 'TRY_005'
  | 'TRY_001';

export type TenderMethod =
  | 'cash'
  | 'card'
  | 'gift_card'
  | 'transfer'
  | 'on_account'
  | 'loyalty_points';

export interface TryDenomination {
  readonly code: TryDenominationCode;
  readonly label: string;
  readonly unitMinor: number;
  readonly kind: 'note' | 'coin';
}

export interface DayClosePrerequisite {
  readonly key:
    | 'parked_orders'
    | 'open_orders'
    | 'pending_card_capture'
    | 'clocked_in_staff'
    | 'unadjusted_tips';
  readonly label: string;
  readonly status: 'ok' | 'warning' | 'blocked';
  readonly detail: string;
  readonly count: number;
  readonly actionRoute: string | null;
}

export interface DenominationCountEntry {
  readonly code: TryDenominationCode;
  readonly count: number;
}

export interface NonCashTenderCount {
  readonly method: Exclude<TenderMethod, 'cash'>;
  readonly expectedMinor: number;
  readonly countedMinor: number;
  readonly transactionCount: number;
  readonly referenceSlip: string | null;
}

export interface VarianceRow {
  readonly method: TenderMethod;
  readonly expectedMinor: number;
  readonly countedMinor: number;
  readonly varianceMinor: number;
  readonly withinTolerance: boolean;
}

export interface VarianceTolerancePolicy {
  readonly cashMinor: number;
  readonly perTenderMinor: number;
  readonly hardBlockMinor: number;
}

export interface ZReportTenderRow {
  readonly method: TenderMethod;
  readonly amountMinor: number;
  readonly count: number;
}

export interface ZReportCategoryRow {
  readonly key: string;
  readonly label: string;
  readonly amountMinor: number;
}

export interface ZReportPayload {
  readonly branchName: string;
  readonly businessDate: string;
  readonly closedAt: string;
  readonly closedByName: string;
  readonly grossMinor: number;
  readonly discountMinor: number;
  readonly taxMinor: number;
  readonly netMinor: number;
  readonly refundMinor: number;
  readonly voidedMinor: number;
  readonly tenders: readonly ZReportTenderRow[];
  readonly categories: readonly ZReportCategoryRow[];
  readonly denominations: readonly DenominationCountEntry[];
  readonly override: {
    readonly managerName: string | null;
    readonly reasonCode: string | null;
    readonly explanation: string | null;
  } | null;
}

export interface DayCloseSnapshot {
  readonly sessionId: string;
  readonly branchId: string;
  readonly branchName: string;
  readonly businessDate: string;
  readonly cashierSessionId: string;
  readonly state: DayCloseWizardState;
  readonly currentStep: DayCloseWizardStep;
  readonly version: number;
  readonly openedByName: string;
  readonly openedAt: string;
  readonly submittedAt: string | null;
  readonly closedAt: string | null;
  readonly prerequisites: readonly DayClosePrerequisite[];
  readonly cash: {
    readonly openingFloatMinor: number;
    readonly expectedCashMinor: number;
    readonly countedCashMinor: number;
    readonly denominationCounts: readonly DenominationCountEntry[];
  };
  readonly nonCashTenders: readonly NonCashTenderCount[];
  readonly variance: {
    readonly rows: readonly VarianceRow[];
    readonly totalAbsMinor: number;
    readonly withinTolerance: boolean;
    readonly requiresManagerOverride: boolean;
    readonly requiresOwnerOverride: boolean;
  };
  readonly override: {
    readonly present: boolean;
    readonly managerName: string | null;
    readonly reasonCode: string | null;
    readonly explanation: string | null;
  };
  readonly deposit: {
    readonly closingFloatMinor: number;
    readonly depositAmountMinor: number;
  };
  readonly tolerance: VarianceTolerancePolicy;
  readonly zReportPayload: ZReportPayload | null;
}

export interface ManagerOverrideInput {
  readonly managerPin: string;
  readonly reasonCode: string;
  readonly explanation: string | null;
}

// ===== Label dictionary =====
export interface DayCloseLabels {
  page: {
    title: string;
    subtitle: string;
    breadcrumb: string;
    backToCashier: string;
    branchLabel: string;
    dateLabel: string;
    openedByLabel: string;
    openedAtLabel: string;
  };
  cta: {
    start: string;
    openWizard: string;
    continueDraft: string;
    viewZReport: string;
    reopen: string;
    back: string;
    next: string;
    confirm: string;
    cancel: string;
    retry: string;
  };
  steps: {
    prerequisites: {
      key: string;
      title: string;
      description: string;
      empty: string;
      blocked: string;
      action: string;
      items: Record<string, string>;
    };
    cash: {
      key: string;
      title: string;
      description: string;
      expected: string;
      counted: string;
      openingFloat: string;
      denomination: string;
      count: string;
      subtotal: string;
      tip: string;
    };
    non_cash: {
      key: string;
      title: string;
      description: string;
      method: string;
      expected: string;
      counted: string;
      reference: string;
      referencePlaceholder: string;
      txCount: string;
      methods: Record<string, string>;
    };
    review: {
      key: string;
      title: string;
      description: string;
      variance: string;
      total: string;
      withinTolerance: string;
      outOfTolerance: string;
      requiresManagerOverride: string;
      requiresOwnerOverride: string;
      depositLabel: string;
      closingFloatLabel: string;
      depositHelp: string;
    };
    submit: {
      key: string;
      title: string;
      description: string;
      submitCta: string;
      submitting: string;
      successTitle: string;
      successDescription: string;
      zReport: string;
      download: string;
      print: string;
    };
  };
  summary: { expected: string; counted: string; variance: string; status: string };
  denominations: Record<string, string>;
  variance: { over: string; short: string; match: string; rowDetail: string };
  override: {
    title: string;
    description: string;
    managerPin: string;
    managerPinPlaceholder: string;
    reasonCode: string;
    reasonCodes: Record<string, string>;
    explanation: string;
    explanationPlaceholder: string;
    submit: string;
    errorInvalidPin: string;
    errorExplanation: string;
  };
  reopen: {
    title: string;
    description: string;
    reasonLabel: string;
    reasonPlaceholder: string;
    submit: string;
    success: string;
  };
  errors: {
    sessionNotFound: string;
    networkOffline: string;
    managerOverrideRequired: string;
    ownerOverrideRequired: string;
    submitFailed: string;
    mobileBlocked: string;
  };
  toast: {
    draftSaved: string;
    cashPatched: string;
    nonCashPatched: string;
    depositPatched: string;
    submitted: string;
    reopened: string;
  };
  zReportLabels: {
    branch: string;
    date: string;
    closedBy: string;
    closedAt: string;
    gross: string;
    discount: string;
    tax: string;
    net: string;
    refund: string;
    voided: string;
    tenders: string;
    categories: string;
    denominations: string;
    override: string;
  };
  currencyPrefix: string;
}

export const TRY_DENOMINATIONS: readonly TryDenomination[] = [
  { code: 'TRY_200', label: '₺200', unitMinor: 20000, kind: 'note' },
  { code: 'TRY_100', label: '₺100', unitMinor: 10000, kind: 'note' },
  { code: 'TRY_50', label: '₺50', unitMinor: 5000, kind: 'note' },
  { code: 'TRY_20', label: '₺20', unitMinor: 2000, kind: 'note' },
  { code: 'TRY_10', label: '₺10', unitMinor: 1000, kind: 'note' },
  { code: 'TRY_5', label: '₺5', unitMinor: 500, kind: 'note' },
  { code: 'TRY_1', label: '₺1', unitMinor: 100, kind: 'coin' },
  { code: 'TRY_050', label: '50 kr', unitMinor: 50, kind: 'coin' },
  { code: 'TRY_025', label: '25 kr', unitMinor: 25, kind: 'coin' },
  { code: 'TRY_010', label: '10 kr', unitMinor: 10, kind: 'coin' },
  { code: 'TRY_005', label: '5 kr', unitMinor: 5, kind: 'coin' },
  { code: 'TRY_001', label: '1 kr', unitMinor: 1, kind: 'coin' },
];
